// ============================================================================
// LA COPIE AU CLIC — ce qu'elle coûte, ce qu'elle dit, et POURQUOI elle a besoin d'un repli.
//
// ⚠️ LE FAIT QUI COMMANDE TOUT : `navigator.clipboard` N'EXISTE PAS hors contexte sécurisé. En HTTP —
// donc sur tout le réseau interne de bêta — l'API est absente, et les trois copies écrites dans le
// dépôt échouent : l'une en silence (`catch` vide), une autre en MENTANT (toast de succès émis sur la
// même ligne que l'appel, avant tout résultat). D'où la chaîne de repli, et d'où le fait que le
// résultat soit un objet qu'on est OBLIGÉ de lire.
//
// ⚠️ CE MODULE EST PUR : aucun DOM au moment de l'import, aucun React. La chaîne de repli se teste
// sans navigateur, et c'est le seul moyen de prouver qu'un échec est bien rendu comme un échec.
// ============================================================================

/**
 * Durées et géométrie du retour visuel.
 * ⚠️ L'ÉCHEC DURE PLUS LONGTEMPS QUE LE SUCCÈS, et ce n'est pas une coquetterie : un succès se
 * confirme d'un coup d'œil, un échec se LIT — il faut avoir le temps de comprendre qu'il va falloir
 * sélectionner la valeur à la main.
 */
export const COPY_FEEDBACK = {
  okMs: 1600,
  errMs: 6000,
  icon: 11,
  gap: 5,
  /** Zone de clic élargie, puis compensée par une marge négative : le texte ne se décale pas. */
  hitPad: '1px 5px',
  hitMargin: '-1px -5px',
  /** L'icône s'efface au repos (la VALEUR est l'information) et revient au survol ET au focus clavier. */
  iconRestOpacity: 0.38,
  /**
   * L'INFOBULLE DE CONFIRMATION, relevée sur la maquette (2026-08-16).
   * ⚠️ HORS FLUX et `pointer-events: none` : elle apparaît au-dessus du déclencheur, où qu'il soit —
   * y compris dans une case de 82 px, où un message en flux ferait exploser la mise en page.
   * ⚠️ `holdMs` est court (950 ms) : une confirmation se lit d'un coup d'œil. L'ÉCHEC, lui, garde
   * `errMs` — il faut avoir le temps de comprendre qu'il va falloir sélectionner la valeur à la main.
   */
  bubble: { padV: 5, padH: 9, radius: 7, fontSize: 11, weight: 700, offsetY: 30, fadeMs: 180, holdMs: 950 },
} as const;

/**
 * Le résultat d'une copie — un SUCCÈS ou un échec QUALIFIÉ, jamais un booléen.
 * ⚠️ `reason` porte de quoi écrire une phrase utile : « connexion non sécurisée » appelle « sélectionnez
 * la valeur à la main », « refusée par le navigateur » appelle le message réel du navigateur. Un
 * `false` nu ne permet que « échec », qui n'aide personne.
 */
export type CopyResult =
  | { ok: true; via: 'clipboard' | 'execCommand' }
  | { ok: false; reason: 'insecure' | 'denied' | 'unsupported'; message: string };

/** Ce qu'on attend du navigateur — injectable, donc testable sans DOM. */
export interface CopyEnv {
  writeText?: (value: string) => Promise<void>;
  /** Repli historique. Rend le booléen de `document.execCommand('copy')`. */
  legacyCopy?: (value: string) => boolean;
  /** `window.isSecureContext` — décide du MESSAGE, jamais du chemin d'essai. */
  secureContext: boolean;
}

const MSG_INSECURE =
  'Copie impossible depuis une connexion non sécurisée (HTTP) : sélectionnez la valeur pour la copier à la main.';
const MSG_UNSUPPORTED =
  'Ce navigateur ne permet pas la copie automatique : sélectionnez la valeur pour la copier à la main.';

/**
 * Copie, avec repli — et un ÉCHEC QUI SE DIT.
 *
 * L'ordre exact, et le pourquoi de chaque marche :
 *  1. `navigator.clipboard` **s'il existe** : le chemin normal, le seul asynchrone.
 *  2. `document.execCommand('copy')` : il fonctionne en HTTP, là où le premier n'existe même pas.
 *     ⚠️ SON BOOLÉEN EST LU — c'est précisément ce que la maquette ne fait pas, et c'est ce qui
 *     transforme un échec en faux succès.
 *  3. Les deux ont échoué → on rend l'échec avec sa cause, jamais un succès optimiste.
 */
export async function copyWith(env: CopyEnv, value: string): Promise<CopyResult> {
  if (env.writeText) {
    try {
      await env.writeText(value);
      return { ok: true, via: 'clipboard' };
    } catch (e) {
      // ⚠️ ON NE REND PAS ENCORE L'ÉCHEC : un refus de permission n'empêche pas le repli de marcher.
      // Le message réel est gardé pour le cas où le repli échoue AUSSI.
      const message = e instanceof Error ? e.message : String(e);
      const secours = tenterLegacy(env, value);
      if (secours) return secours;
      return { ok: false, reason: 'denied', message: `Copie refusée par le navigateur : ${message}` };
    }
  }

  const secours = tenterLegacy(env, value);
  if (secours) return secours;
  return env.secureContext
    ? { ok: false, reason: 'unsupported', message: MSG_UNSUPPORTED }
    : { ok: false, reason: 'insecure', message: MSG_INSECURE };
}

/** Le repli, isolé : il rend `null` quand il n'a pas pu, jamais un faux succès. */
function tenterLegacy(env: CopyEnv, value: string): CopyResult | null {
  if (!env.legacyCopy) return null;
  try {
    return env.legacyCopy(value) ? { ok: true, via: 'execCommand' } : null;
  } catch {
    return null;
  }
}
